import React, { useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowIcon from "assets/icons/arrowIcon.svg";
import { makeStyles } from "tss-react/mui";
import ColourConstants from "helpers/colourConstants";
import useLanguageSettings, { BASELINE } from "./useLanguageSettings";
import { localeLabel } from "./localeLabel";

const useStyles = makeStyles()(() => ({
	detailContainer: {
		marginTop: "25px !important",
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	sectionHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "17px",
	},
}));

const LanguageSection = ({ siteId, isReadOnly }) => {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(true);
	const {
		available,
		defaultLocale,
		changeDefault,
		enabled,
		toggleEnabled,
		loading,
		saving,
	} = useLanguageSettings(siteId);

	const enabledOptions = [...new Set([BASELINE, ...enabled])];

	return (
		<Accordion
			className={classes.detailContainer}
			expanded={isExpanded}
			onChange={() => setIsExpanded(!isExpanded)}
			style={{ padding: 0 }}
		>
			<AccordionSummary
				expandIcon={
					<img alt="Expand icon" src={ArrowIcon} className={classes.expandIcon} />
				}
				aria-controls="language-section-content"
				id="language-section-header"
			>
				<Typography className={classes.sectionHeading}>
					{t("title.languageSettings")}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				{loading ? (
					<CircularProgress />
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 520 }}>
						<Box>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								{t("label.availableLanguages")}
							</Typography>
							{available.map((tag) => (
								<FormControlLabel
									key={tag}
									control={
										<Checkbox
											checked={tag === BASELINE || enabled.includes(tag)}
											disabled={isReadOnly || saving || tag === BASELINE}
											onChange={() => toggleEnabled(tag)}
										/>
									}
									label={
										tag === BASELINE
											? t("label.localeAlwaysOn", { locale: localeLabel(tag) })
											: localeLabel(tag)
									}
								/>
							))}
						</Box>

						<Box>
							<TextField
								select
								size="small"
								fullWidth
								label={t("label.defaultLanguage")}
								value={defaultLocale}
								disabled={isReadOnly || saving}
								onChange={(e) => changeDefault(e.target.value)}
							>
								{enabledOptions.map((tag) => (
									<MenuItem key={tag} value={tag}>
										{localeLabel(tag)}
									</MenuItem>
								))}
							</TextField>
						</Box>

					</Box>
				)}
			</AccordionDetails>
		</Accordion>
	);
};

export default LanguageSection;
